<template>
  <v-container fluid class="aml-dash pa-5">

    <!-- ══ Page loading skeleton ══ -->
    <template v-if="pageLoading">
      <div class="d-flex align-center justify-space-between mb-5">
        <v-skeleton-loader type="heading" width="220" />
        <v-skeleton-loader type="button" width="200" />
      </div>
      <v-row dense class="mb-5">
        <v-col v-for="i in 4" :key="i" cols="6" sm="3">
          <v-skeleton-loader type="card" height="90" />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" md="8"><v-skeleton-loader type="card" height="380" /></v-col>
        <v-col cols="12" md="4"><v-skeleton-loader type="card" height="180" class="mb-3" /><v-skeleton-loader type="card" height="180" /></v-col>
      </v-row>
    </template>

    <!-- ══ Header ══ -->
    <template v-else>
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="aml-overline mb-1">AML Pro &middot; {{ currentMonth }}</div>
        <h2 class="aml-title">Watchlist Screening</h2>
      </div>
      <div class="d-flex align-center" style="gap:8px">
        <v-btn depressed small class="aml-btn-ghost" :loading="loading" @click="loadStats">
          <v-icon small left>mdi-refresh</v-icon>Refresh
        </v-btn>
        <v-btn depressed small class="aml-btn-ghost" @click="goToSearch">
          <v-icon small left>mdi-magnify</v-icon>New search
        </v-btn>
        <v-btn depressed small class="aml-btn-primary" @click="goToBulkSearch">
          <v-icon small left>mdi-magnify-plus-outline</v-icon>Bulk search
        </v-btn>
      </div>
    </div>

    <!-- ══ Metric cards ══ -->
    <v-row dense class="mb-5">
      <v-col cols="6" sm="3">
        <div
          class="aml-metric-card"
          :class="{ 'aml-metric-active aml-metric-red': tableFilter === 'unreviewed' }"
          @click="setTableFilter('unreviewed')"
        >
          <div class="aml-metric-label">Unreviewed</div>
          <div class="aml-metric-value" style="color:#E24B4A">{{ stats.metrics && stats.metrics.unreviewed }}</div>
          <div class="aml-metric-sub">Possible matches awaiting review</div>
        </div>
      </v-col>
      <v-col cols="6" sm="3">
        <div
          class="aml-metric-card"
          :class="{ 'aml-metric-active aml-metric-blue': tableFilter === 'underReview' }"
          @click="setTableFilter('underReview')"
        >
          <div class="aml-metric-label">Under review</div>
          <div class="aml-metric-value" style="color:#185FA5">{{ stats.metrics && stats.metrics.underReview }}</div>
          <div class="aml-metric-sub">Active investigations</div>
        </div>
      </v-col>
      <v-col cols="6" sm="3">
        <div
          class="aml-metric-card"
          :class="{ 'aml-metric-active aml-metric-red2': tableFilter === 'truePositive' }"
          @click="setTableFilter('truePositive')"
        >
          <div class="aml-metric-label">Confirmed hits</div>
          <div class="aml-metric-value" style="color:#A32D2D">{{ stats.metrics && stats.metrics.truePositive }}</div>
          <div class="aml-metric-sub">True positives</div>
        </div>
      </v-col>
      <v-col cols="6" sm="3">
        <div
          class="aml-metric-card"
          :class="{ 'aml-metric-active aml-metric-green': tableFilter === 'falsePositive' }"
          @click="setTableFilter('falsePositive')"
        >
          <div class="aml-metric-label">Cleared</div>
          <div class="aml-metric-value" style="color:#3B6D11">{{ stats.metrics && stats.metrics.falsePositive }}</div>
          <div class="aml-metric-sub">False positives</div>
        </div>
      </v-col>
    </v-row>

    <!-- ══ Main grid ══ -->
    <v-row dense>

      <!-- ── Left: flags table ── -->
      <v-col cols="12" md="8">
        <div class="aml-card">

          <!-- Toolbar -->
          <div class="aml-toolbar">
            <div class="d-flex align-center" style="gap:6px;flex-wrap:wrap">
              <button
                v-for="tab in filterTabs"
                :key="tab.key"
                class="aml-filter-tab"
                :class="tableFilter === tab.key ? 'aml-filter-tab--active' : ''"
                :style="tableFilter === tab.key ? `border-color:${tab.color};background:${tab.color}18;color:${tab.color}` : ''"
                @click="setTableFilter(tab.key)"
              >
                {{ tab.label }}
                <span
                  class="aml-filter-count"
                  :style="tableFilter === tab.key ? `background:${tab.color}28;color:${tab.color}` : ''"
                >{{ tab.count }}</span>
              </button>
            </div>
            <div class="d-flex align-center" style="gap:8px">
              <div class="aml-search-wrap">
                <v-icon x-small class="aml-search-icon">mdi-magnify</v-icon>
                <input v-model="tableSearch" class="aml-search-input" placeholder="Search flags..." />
              </div>
              <v-btn depressed x-small class="aml-btn-ghost" @click="filterDialog = true">
                <v-icon x-small left>mdi-filter-outline</v-icon>More filters
              </v-btn>
            </div>
          </div>

          <!-- Table -->
          <div class="aml-table-wrap">
            <table class="aml-table">
              <colgroup>
                <col style="width:18%">
                <col style="width:14%">
                <col style="width:12%">
                <col style="width:14%">
                <col style="width:12%">
                <col style="width:18%">
                <col style="width:12%">
              </colgroup>
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Provider</th>
                  <th>List</th>
                  <th>Status</th>
                  <th>Last search by</th>
                  <th>Flagged</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!filteredFlags.length">
                  <td colspan="7" class="aml-empty-row">No flags match this filter</td>
                </tr>
                <tr
                  v-for="flag in filteredFlags"
                  :key="flag.id"
                  class="aml-table-row"
                  @click="goToFlag(flag.id)"
                >
                  <td>
                    <div style="font-size:12px;font-weight:500">{{ flagPrimaryPerson(flag) }}</div>
                    <div class="aml-muted" style="font-size:10px">{{ flag.mainAccountNumber }}</div>
                  </td>
                  <td>
                    <span class="aml-type-badge">{{ providerShort(flag.provider) }}</span>
                  </td>
                  <td class="aml-muted" style="font-size:11px">{{ flag.listName || '—' }}</td>
                  <td>
                    <span class="aml-pill" :style="flagStatusStyle(flag.flagStatus)">
                      {{ flagStatusLabel(flag.flagStatus) }}
                    </span>
                  </td>
                  <td>
                    <!-- Re-search override pending -->
                    <v-tooltip v-if="flag.allowResearch" bottom>
                      <template #activator="{ on }">
                        <span class="aml-pill" style="background:#FFF4E5;color:#854F0B" v-on="on">
                          <v-icon x-small left>mdi-refresh</v-icon>Override set
                        </span>
                      </template>
                      <span>Re-search override is active — next search will re-screen this flag</span>
                    </v-tooltip>
                    <!-- Last searcher -->
                    <template v-else-if="flag.searchLogs && flag.searchLogs.length">
                      <div style="font-size:11px;font-weight:500">
                        {{ flag.searchLogs[0].triggeredBy ? assigneeName(flag.searchLogs[0].triggeredBy) : 'System' }}
                      </div>
                      <div class="aml-muted" style="font-size:10px">{{ fmtDate(flag.searchLogs[0].dateCreated) }}</div>
                    </template>
                    <span v-else class="aml-muted" style="font-size:11px">—</span>
                  </td>
                  <td class="aml-muted">{{ fmtDate(flag.dateCreated) }}</td>
                  <td @click.stop>
                    <div class="d-flex align-center justify-end" style="gap:4px">
                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <button class="aml-icon-btn" v-on="on" @click="goToFlag(flag.id)">
                            <v-icon x-small color="#185FA5">mdi-eye</v-icon>
                          </button>
                        </template>
                        <span>View flag</span>
                      </v-tooltip>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="aml-table-footer">
            <span class="aml-muted">
              {{ flagsTotal }} flag{{ flagsTotal !== 1 ? 's' : '' }} total
              <span v-if="flagsTotal > flagsLimit"> · Page {{ flagsPage }} of {{ Math.ceil(flagsTotal / flagsLimit) }}</span>
            </span>
            <div v-if="flagsTotal > flagsLimit" class="d-flex align-center" style="gap:4px">
              <v-btn x-small text :disabled="flagsPage <= 1" @click="flagsPrevPage">
                <v-icon x-small>mdi-chevron-left</v-icon>Prev
              </v-btn>
              <v-btn x-small text :disabled="flagsPage * flagsLimit >= flagsTotal" @click="flagsNextPage">
                Next<v-icon x-small>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </v-col>

      <!-- ── Right panel ── -->
      <v-col cols="12" md="4">

        <!-- Searches today -->
        <div class="aml-card mb-3">
          <div class="aml-panel-header">
            <span>Recent flags</span>
            <span class="aml-count-pill aml-count-blue">{{ recentFlags.length }}</span>
          </div>
          <div v-if="!recentFlags.length" class="aml-empty-panel">No recent flags.</div>
          <div
            v-for="f in pagedRecentFlags"
            :key="f.id"
            class="aml-my-case-row"
            @click="goToFlag(f.id)"
          >
            <div>
              <div class="aml-ref-link" style="font-size:12px">{{ flagPrimaryPerson(f) }}</div>
              <div class="aml-muted" style="font-size:10px;margin-top:1px">{{ f.mainAccountNumber }}</div>
              <div class="aml-muted" style="font-size:11px;margin-top:2px">{{ providerShort(f.provider) }} · {{ flagStatusLabel(f.flagStatus) }}</div>
            </div>
            <v-icon x-small color="#ccc">mdi-chevron-right</v-icon>
          </div>
          <!-- Pagination -->
          <div v-if="recentFlags.length > recentFlagsLimit" class="aml-panel-footer">
            <span class="aml-muted" style="font-size:10px">
              {{ (recentFlagsPage - 1) * recentFlagsLimit + 1 }}–{{ Math.min(recentFlagsPage * recentFlagsLimit, recentFlags.length) }}
              of {{ recentFlags.length }}
            </span>
            <div class="d-flex" style="gap:2px">
              <v-btn icon x-small :disabled="recentFlagsPage <= 1" @click="recentFlagsPage--">
                <v-icon x-small>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn icon x-small :disabled="recentFlagsPage * recentFlagsLimit >= recentFlags.length" @click="recentFlagsPage++">
                <v-icon x-small>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Watchlist list status -->
        <div class="aml-card mb-3">
          <div class="aml-panel-header">
            <span>Watchlists</span>
            <span
              class="aml-count-pill"
              :style="sourcesNeedingAttention.length ? 'background:#FFF4E5;color:#854F0B' : 'background:#EAF3DE;color:#3B6D11'"
            >
              {{ sourcesNeedingAttention.length ? sourcesNeedingAttention.length + ' need attention' : 'All current' }}
            </span>
          </div>
          <div v-if="!sources.length" class="aml-empty-panel">List status unavailable.</div>
          <div v-for="s in sources" :key="s.source_code" class="aml-source-row">
            <v-tooltip bottom max-width="300">
              <template #activator="{ on }">
                <div v-on="on" class="d-flex align-center" style="gap:6px;min-width:0">
                  <v-icon x-small :color="s.needs_attention ? '#D85A30' : '#3B6D11'">
                    {{ s.needs_attention ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline' }}
                  </v-icon>
                  <span style="font-size:11px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ s.label }}</span>
                </div>
              </template>
              <span style="font-size:11px">
                <template v-if="s.list_updated_at">List date: {{ describeListDate(s.list_updated_at, s.date_method) }}<br></template>
                <template v-if="s.fetched_at">Last checked: {{ fmtDate(s.fetched_at) }}<br></template>
                <template v-if="s.record_count">{{ s.record_count.toLocaleString() }} records<br></template>
                <template v-if="s.attention_reason"><strong>{{ s.attention_reason }}</strong></template>
              </span>
            </v-tooltip>
            <span class="aml-muted" style="font-size:10px;flex-shrink:0">{{ isoDate(s.list_updated_at) || '—' }}</span>
          </div>
        </div>

        <!-- Open watchlist cases — full table -->
        <div class="aml-card mb-3">
          <div class="aml-panel-header">
            <span>Active cases</span>
            <div class="d-flex align-center" style="gap:6px">
              <span class="aml-count-pill aml-count-blue">{{ openCases.length }}</span>
              <v-btn text x-small color="primary" @click="goToCases">View all</v-btn>
            </div>
          </div>

          <div v-if="casesLoading" class="pa-3">
            <v-skeleton-loader type="list-item-two-line" v-for="i in 3" :key="i" class="mb-1" />
          </div>

          <div v-else-if="!openCases.length" class="aml-empty-panel">No active watchlist cases.</div>

          <table v-else class="aml-table">
            <colgroup>
              <col style="width:30%">
              <col style="width:28%">
              <col style="width:22%">
              <col style="width:20%">
            </colgroup>
            <thead>
              <tr>
                <th>Case</th>
                <th>Account / Name</th>
                <th>Stage</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="c in openCases"
                :key="c.id"
                class="aml-table-row"
                @click="goToWatchlistCase(c.id)"
              >
                <!-- Reference + status -->
                <td>
                  <div class="aml-ref-link" style="font-size:12px">{{ c.caseReference }}</div>
                  <div class="d-flex align-center" style="gap:4px;margin-top:2px;flex-wrap:wrap">
                    <span class="aml-pill" :style="caseStatusStyle(c.status)" style="font-size:9px">
                      {{ caseStatusLabel(c.status) }}
                    </span>
                    <span v-if="c.hasNewSearchActivity" class="aml-pill" style="background:#FFF4E5;color:#854F0B;font-size:9px">
                      <v-icon x-small left>mdi-bell-outline</v-icon>New activity
                    </span>
                  </div>
                </td>

                <!-- Account + person name -->
                <td>
                  <div style="font-size:12px;font-weight:500">{{ casePrimaryPerson(c) }}</div>
                  <div class="aml-muted" style="font-size:10px">{{ c.account && c.account.mainAccountNumber }}</div>
                </td>

                <!-- Stage + provider -->
                <td>
                  <div class="aml-muted" style="font-size:11px">{{ c.reportStage || 'Open' }}</div>
                  <div class="aml-muted" style="font-size:10px">
                    {{ c.watchlistFlag && providerShort(c.watchlistFlag.provider) }}
                  </div>
                </td>

                <!-- Action -->
                <td @click.stop>
                  <v-btn icon x-small @click="goToWatchlistCase(c.id)">
                    <v-icon x-small color="#185FA5">mdi-eye</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </v-col>
    </v-row>

    <!-- ══ Filter Dialog ══ -->
    <v-dialog v-model="filterDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon left color="primary">mdi-filter-outline</v-icon>
            <span class="subtitle-1">Filter flags</span>
          </div>
          <v-btn icon @click="filterDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-select
            v-model="filters.flagStatus"
            :items="statusOptions"
            item-text="text"
            item-value="value"
            label="Flag status"
            clearable dense outlined
            prepend-inner-icon="mdi-list-status"
            class="mb-3"
          />
          <v-select
            v-model="filters.provider"
            :items="providerOptions"
            item-text="text" item-value="value"
            label="Provider"
            clearable dense outlined
            prepend-inner-icon="mdi-database-outline"
            class="mb-3"
          />
          <v-text-field v-model="filters.dateFrom" label="Date from" type="date" dense outlined prepend-inner-icon="mdi-calendar-start" class="mb-3" />
          <v-text-field v-model="filters.dateTo"   label="Date to"   type="date" dense outlined prepend-inner-icon="mdi-calendar-end" />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn text color="grey darken-1" @click="clearFilters"><v-icon left small>mdi-close</v-icon>Clear</v-btn>
          <v-spacer />
          <v-btn text @click="filterDialog = false">Cancel</v-btn>
          <v-btn depressed color="primary" :loading="tableLoading" @click="applyFilters">
            <v-icon left small>mdi-magnify</v-icon>Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </template>
  </v-container>
</template>

<script>
import { mapState }        from 'vuex';
import PageMixin           from '@/mixins/page.mixin';
import FormatMixin         from '@/mixins/format.mixin';
import AmlMixin            from '@/mixins/aml.mixin';
import { WatchlistService } from '@/modules/amlpro/services/watchlist.service';
import {
  providerItems, providerShort, describeListDate, isoDate,
} from '@/modules/amlpro/services/watchlist-sources';

const svc = new WatchlistService();

const EMPTY_STATS = {
  metrics: { unreviewed: 0, underReview: 0, truePositive: 0, falsePositive: 0, searchesToday: 0, requiresAttention: 0 },
  recentFlags: [],
};

export default {
  name: 'WatchlistDashboard',
  mixins: [PageMixin, FormatMixin, AmlMixin],

  data() {
    return {
      pageLoading:  true,
      loading:      false,
      tableLoading: false,
      casesLoading: false,
      stats:        { ...EMPTY_STATS },
      flags:        [],
      flagsTotal:   0,
      flagsPage:    1,
      flagsLimit:   15,
      openCases:    [],
      tableFilter:  'unreviewed',
      tableSearch:  '',
      filterDialog: false,
      filters:      { flagStatus: null, provider: '', dateFrom: null, dateTo: null },
      sources:      [],
      recentFlagsPage: 1,
      recentFlagsLimit: 5,
      statusOptions: [
        { text: 'Unreviewed',   value: 'POSSIBLE_MATCH_UNREVIEWED' },
        { text: 'Under review', value: 'UNDER_REVIEW' },
        { text: 'Confirmed hit', value: 'TRUE_POSITIVE' },
        { text: 'Cleared',      value: 'FALSE_POSITIVE' },
      ],
    };
  },

  computed: {
    ...mapState('authModule', { loggedInUser: 'user' }),

    currentMonth() {
      return new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    },

    providerOptions() {
      return [...providerItems(this.sources), { text: 'UN (older searches)', value: 'UN' }];
    },

    sourcesNeedingAttention() {
      return this.sources.filter(s => s.needs_attention);
    },

    recentFlags() {
      return this.stats.recentFlags ?? [];
    },

    pagedRecentFlags() {
      const start = (this.recentFlagsPage - 1) * this.recentFlagsLimit;
      return this.recentFlags.slice(start, start + this.recentFlagsLimit);
    },

    filterTabs() {
      const m = this.stats.metrics ?? {};
      return [
        { key: 'unreviewed',  label: 'Unreviewed',  count: m.unreviewed   ?? 0, color: '#E24B4A' },
        { key: 'underReview', label: 'Under review', count: m.underReview  ?? 0, color: '#185FA5' },
        { key: 'truePositive',  label: 'Confirmed', count: m.truePositive  ?? 0, color: '#A32D2D' },
        { key: 'falsePositive', label: 'Cleared',   count: m.falsePositive ?? 0, color: '#3B6D11' },
      ];
    },

    filteredFlags() {
      let list = this.flags;
      const q  = this.tableSearch.toLowerCase().trim();
      if (q) {
        list = list.filter(f =>
          f.mainAccountNumber?.toLowerCase().includes(q) ||
          f.provider?.toLowerCase().includes(q) ||
          f.listName?.toLowerCase().includes(q),
        );
      }
      return list;
    },
  },

  async mounted() {
    await Promise.all([
      this.loadStats(),
      this.loadFlags(),
      this.loadOpenCases(),
    ]);
    this.pageLoading = false;
  },

  methods: {
    providerShort, describeListDate, isoDate,

    async loadSources() {
      try {
        const res    = await svc.fetchSources();
        const data   = res.data ?? res;
        this.sources = Array.isArray(data) ? data : [];
      } catch { this.sources = []; }
    },

    async loadStats() {
      this.loading = true;
      this.loadSources();
      try {
        const res   = await svc.fetchDashboardStats();
        this.stats  = res.data ?? res;
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load dashboard' });
      } finally {
        this.loading = false;
      }
    },

    async loadFlags() {
      this.tableLoading = true;
      try {
        const statusMap = {
          unreviewed:   'POSSIBLE_MATCH_UNREVIEWED',
          underReview:  'UNDER_REVIEW',
          truePositive: 'TRUE_POSITIVE',
          falsePositive:'FALSE_POSITIVE',
        };
        const params = { page: this.flagsPage, limit: this.flagsLimit };
        if (statusMap[this.tableFilter]) params.flagStatus = statusMap[this.tableFilter];
        if (this.filters.provider)  params.provider  = this.filters.provider;
        if (this.filters.dateFrom)  params.dateFrom  = this.filters.dateFrom;
        if (this.filters.dateTo)    params.dateTo    = this.filters.dateTo;

        const res          = await svc.fetchFlags(params);
        const data         = res.data ?? res;
        this.flags         = data.data ?? data;
        this.flagsTotal    = data.total ?? this.flags.length;
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load flags' });
      } finally {
        this.tableLoading = false;
      }
    },

    async loadOpenCases() {
      this.casesLoading = true;
      try {
        const res      = await svc.fetchCases({ limit: 20 });
        const data     = res.data ?? res;
        const list     = data.data ?? data;
        this.openCases = list.filter(c => ['OPEN', 'REOPENED'].includes(c.status));
      } catch { this.openCases = []; }
      finally { this.casesLoading = false; }
    },

    setTableFilter(f) {
      this.tableFilter = f;
      this.tableSearch = '';
      this.flagsPage   = 1;
      this.loadFlags();
    },

    flagsPrevPage() {
      if (this.flagsPage > 1) { this.flagsPage--; this.loadFlags(); }
    },

    flagsNextPage() {
      if (this.flagsPage * this.flagsLimit < this.flagsTotal) {
        this.flagsPage++;
        this.loadFlags();
      }
    },

    applyFilters() {
      this.filterDialog = false;
      this.flagsPage    = 1;
      this.loadFlags();
    },

    clearFilters() {
      this.filters = { flagStatus: null, provider: '', dateFrom: null, dateTo: null };
      this.filterDialog = false;
      this.loadFlags();
    },

    flagStatusLabel(s) {
      const map = {
        POSSIBLE_MATCH_UNREVIEWED: 'Unreviewed',
        UNDER_REVIEW:              'Under review',
        TRUE_POSITIVE:             'Confirmed hit',
        FALSE_POSITIVE:            'Cleared',
      };
      return map[s] ?? s;
    },

    flagStatusStyle(s) {
      const map = {
        POSSIBLE_MATCH_UNREVIEWED: 'background:#FCEBEB;color:#A32D2D',
        UNDER_REVIEW:              'background:#E6F1FB;color:#185FA5',
        TRUE_POSITIVE:             'background:#FCEBEB;color:#A32D2D',
        FALSE_POSITIVE:            'background:#EAF3DE;color:#3B6D11',
      };
      return map[s] ?? 'background:#F1EFE8;color:#5F5E5A';
    },

    goToFlag(flagId)          { this.$router.push({ name: 'watchlist-flag-detail', params: { flagId } }); },
    goToWatchlistCase(caseId) { this.$router.push({ name: 'watchlist-case-detail', params: { caseId } }); },
    goToSearch()              { this.$router.push({ name: 'watchlist-search' }); },
    goToBulkSearch()          { this.$router.push({ name: 'watchlist-search', query: { mode: 'bulk' } }); },
    goToCases()               { this.$router.push({ name: 'watchlist-cases' }); },

    flagPrimaryPerson(f) {
      const persons = f.account?.person ?? [];
      if (persons.length) {
        const p = persons[0]?.person;
        if (p) return `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || f.mainAccountNumber;
      }
      return f.mainAccountNumber ?? '—';
    },

    casePrimaryPerson(c) {
      const persons = c.account?.person ?? [];
      if (persons.length) {
        const p = persons[0]?.person;
        if (p) return `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || c.account?.mainAccountNumber;
      }
      return c.account?.mainAccountNumber ?? '—';
    },

    caseStatusLabel(s) {
      const map = { OPEN: 'Open', REOPENED: 'Reopened' };
      return map[s] ?? s;
    },

    caseStatusStyle(s) {
      const map = {
        OPEN:     'background:#E6F1FB;color:#185FA5',
        REOPENED: 'background:#FFF4E5;color:#854F0B',
      };
      return map[s] ?? 'background:#f5f5f5;color:#888';
    },

  },
};
</script>

<style scoped>
.aml-dash        { background: transparent; }
.aml-overline    { font-size:11px; font-weight:600; letter-spacing:1px; text-transform:uppercase; color:#999; }
.aml-title       { font-size:22px; font-weight:500; letter-spacing:-0.3px; margin:0; }
.aml-btn-ghost   { font-size:12px !important; border:0.5px solid #ddd !important; background:transparent !important; color:inherit !important; box-shadow:none !important; }
.aml-btn-primary { font-size:12px !important; background:#185FA5 !important; color:#fff !important; box-shadow:none !important; }
.aml-metric-card { border:0.5px solid #e0e0e0; border-radius:12px; padding:16px 20px; cursor:pointer; transition:border-color 0.15s,background 0.15s; background:white; height:100%; }
.aml-metric-card:hover { background:#f7f9fc; }
.aml-metric-active { border-width:1.5px !important; }
.aml-metric-blue   { border-color:#378ADD !important; background:#378ADD12 !important; }
.aml-metric-red    { border-color:#E24B4A !important; background:#E24B4A12 !important; }
.aml-metric-red2   { border-color:#A32D2D !important; background:#A32D2D12 !important; }
.aml-metric-green  { border-color:#3B6D11 !important; background:#3B6D1112 !important; }
.aml-metric-label  { font-size:11px; color:#777; margin-bottom:6px; font-weight:500; }
.aml-metric-value  { font-size:28px; font-weight:600; line-height:1; margin-bottom:6px; }
.aml-metric-sub    { font-size:11px; color:#aaa; }
.aml-card          { border:0.5px solid #e0e0e0; border-radius:12px; overflow:hidden; background:white; }
.aml-toolbar       { padding:12px 16px; border-bottom:0.5px solid #e0e0e0; display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; }
.aml-filter-tab    { font-size:11px; padding:4px 11px; border-radius:20px; border:0.5px solid #ddd; background:transparent; color:#666; cursor:pointer; transition:all 0.12s; display:inline-flex; align-items:center; gap:5px; white-space:nowrap; }
.aml-filter-tab:hover { background:#f5f5f5; }
.aml-filter-tab--active { font-weight:600; }
.aml-filter-count  { font-size:10px; font-weight:700; padding:1px 5px; border-radius:10px; background:#f5f5f5; color:#aaa; }
.aml-search-wrap   { position:relative; }
.aml-search-icon   { position:absolute; left:8px; top:50%; transform:translateY(-50%); color:#bbb; }
.aml-search-input  { font-size:12px; padding:5px 10px 5px 26px; border:0.5px solid #ddd; border-radius:8px; background:white; color:inherit; width:160px; outline:none; font-family:inherit; }
.aml-search-input:focus { border-color:#999; }
.aml-table-wrap    { overflow-x:auto; }
.aml-table         { width:100%; border-collapse:collapse; font-size:12px; table-layout:fixed; }
.aml-table thead tr { border-bottom:0.5px solid #e0e0e0; }
.aml-table th      { padding:9px 14px; text-align:left; font-size:10px; font-weight:600; color:#aaa; letter-spacing:.5px; text-transform:uppercase; }
.aml-table-row     { border-bottom:0.5px solid #e0e0e0; cursor:pointer; transition:background 0.1s; }
.aml-table-row:hover { background:#EBF3FC; }
.aml-table td      { padding:10px 14px; }
.aml-empty-row     { padding:32px 20px; text-align:center; color:#aaa; font-size:13px; }
.aml-table-footer  { padding:9px 16px; border-top:0.5px solid #e0e0e0; font-size:11px; color:#aaa; }
.aml-muted         { color:#777; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.aml-type-badge    { font-size:10px; font-weight:700; color:#666; background:#f5f5f5; padding:2px 6px; border-radius:4px; }
.aml-pill          { display:inline-block; padding:2px 8px; border-radius:20px; font-size:10px; font-weight:500; white-space:nowrap; }
.aml-icon-btn      { width:24px; height:24px; border-radius:6px; border:0.5px solid #e0e0e0; background:transparent; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; padding:0; transition:background 0.1s; }
.aml-icon-btn:hover { background:#f0f0f0; }
.aml-panel-header  { padding:11px 14px; border-bottom:0.5px solid #e0e0e0; display:flex; align-items:center; justify-content:space-between; font-size:12px; font-weight:500; }
.aml-count-pill    { font-size:10px; font-weight:600; padding:2px 8px; border-radius:20px; }
.aml-count-blue    { background:#E6F1FB; color:#185FA5; }
.aml-empty-panel   { padding:16px; text-align:center; color:#bbb; font-size:12px; }
.aml-my-case-row   { padding:9px 14px; border-bottom:0.5px solid #e0e0e0; display:flex; align-items:center; justify-content:space-between; cursor:pointer; transition:background 0.1s; }
.aml-my-case-row:last-child { border-bottom:none; }
.aml-my-case-row:hover { background:#f7f9fc; }
.aml-ref-link      { color:#185FA5; font-weight:600; }
.aml-divider       { height:0.5px; background:#e0e0e0; }
.aml-panel-footer  { padding:6px 12px; border-top:0.5px solid #e0e0e0; display:flex; align-items:center; justify-content:space-between; }
.aml-source-row    { padding:6px 14px; border-bottom:0.5px solid #f0f0f0; display:flex; align-items:center; justify-content:space-between; gap:8px; }
.aml-source-row:last-child { border-bottom:none; }
</style>